import {
  useCallback,
  useMemo,
  ChangeEventHandler,
  useImperativeHandle,
  useRef,
  MutableRefObject,
  FocusEvent,
} from "react";
import { Input, InputProps, forwardRef } from "@chakra-ui/react";
import { ClassNames } from "@emotion/react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import { endOfMonth, formatISO, isValid, parseISO } from "date-fns";

type IsoRepresentation = Required<
  Required<Parameters<typeof formatISO>>[1]
>["representation"];

function getFormattedValue(
  date: null,
  representation?: IsoRepresentation,
): null;
function getFormattedValue(
  date: Date,
  representation?: IsoRepresentation,
): string;
function getFormattedValue(
  date: Date | null,
  representation?: IsoRepresentation,
): string | null;
function getFormattedValue(
  date: [Date | null, Date | null],
  representation?: IsoRepresentation,
): [string | null, string | null];
function getFormattedValue(
  date: Date | [Date | null, Date | null] | null,
  representation?: IsoRepresentation,
): string | [string | null, string | null] | null;
function getFormattedValue(
  date: Date | [Date | null, Date | null] | null,
  representation: IsoRepresentation = "date",
): ISODate | [ISODate | null, ISODate | null] | null {
  if (!date) return date;

  if (Array.isArray(date)) {
    return [getFormattedValue(date[0]), getFormattedValue(date[1])];
  }

  return formatISO(date, { representation });
}

function getMonthEndValue(date: Date | null): Date | null;
function getMonthEndValue(
  date: Date | [Date | null, Date | null] | null,
): Date | [Date | null, Date | null] | null;
function getMonthEndValue(
  date: Date | [Date | null, Date | null] | null,
): Date | [Date | null, Date | null] | null {
  if (!date) return date;

  if (Array.isArray(date)) {
    return [getMonthEndValue(date[0]), getMonthEndValue(date[1])];
  }

  return endOfMonth(date);
}

type Props = Omit<InputProps, "value" | "onChange"> &
  Omit<ReactDatePickerProps, "value" | "selected" | "onChange"> & {
    /** The `react-datepicker` component blur events do not play nicely with the
     * Chakra popover component – blurring the datepicker closes the popover,
     * which is unusable. Setting the prop to true performs a little hack to
     * prevent blur propagation but still close the datepicker. */
    isInPopover?: boolean;
    onlyMonthEnds?: boolean;
    onChange?: (
      value: ISODate | [ISODate | null, ISODate | null] | null,
    ) => void;
    value?: ISODate | null;
  };

const DatePickerInput = forwardRef<Props, "input">(
  (
    {
      isInPopover,
      value,
      onBlur,
      onChange,
      onlyMonthEnds,
      autoComplete = "off",
      ...rest
    },
    ref,
  ) => {
    const isWithTime = rest.showTimeInput || rest.showTimeSelect || false;

    const dateValue = useMemo(() => {
      if (!value) return null;
      const parsed = parseISO(value);
      if (!isValid(parsed)) {
        // eslint-disable-next-line no-console
        console.error(`DatePicker received invalid date: ${value}`);
        return null;
      }
      return parsed;
    }, [value]);

    const handleChange = useCallback<ReactDatePickerProps["onChange"]>(
      (date) => {
        if (onChange) {
          onChange(
            getFormattedValue(
              onlyMonthEnds ? getMonthEndValue(date) : date,
              isWithTime ? "complete" : "date",
            ),
          );
        }
      },
      [onChange, onlyMonthEnds, isWithTime],
    );

    const datePickerRef = useRef<DatePicker>(null);

    const handleBlur = useCallback(
      (evt: FocusEvent<HTMLInputElement>) => {
        if (isInPopover) {
          requestAnimationFrame(() => {
            if (datePickerRef.current?.isCalendarOpen) {
              datePickerRef.current?.setOpen(false);
            }
          });
          evt.preventDefault();
        }
      },
      [isInPopover, datePickerRef],
    );

    useImperativeHandle(ref, () => ({
      blur: () => datePickerRef.current?.setBlur(),
      focus: () => datePickerRef.current?.setFocus(),
    }));

    return (
      <ClassNames>
        {
          // eslint-disable-next-line @typescript-eslint/unbound-method
          ({ css }) => (
            <Input
              ref={
                datePickerRef as unknown as MutableRefObject<HTMLInputElement>
              }
              as={DatePicker}
              wrapperClassName={css`
                width: 100%;
              `}
              showMonthYearPicker={onlyMonthEnds}
              dateFormat={isWithTime ? "Pp" : "P"}
              {...rest}
              selected={dateValue}
              onChange={
                handleChange as unknown as ChangeEventHandler<HTMLInputElement>
              }
              onBlur={handleBlur}
              autoComplete={autoComplete}
              data-1p-ignore={autoComplete === "off" || undefined}
              data-lpignore={autoComplete === "off" || undefined}
            />
          )
        }
      </ClassNames>
    );
  },
);

export default DatePickerInput;
